/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Tooltip } from "@mui/material";
import Badge from "components/UI/Badge/Badge";

import { useRef } from "react";
import {
  MessagesErrorEnum,
  IMessagesError,
  StatusBadgeMapping,
  StatusIconMapping,
  WorkItemResultEnum,
  IRenderToastUnique,
} from "./@types";
import { CircularProgress } from "@material-ui/core";
import { Close, Done, HourglassEmpty } from "@mui/icons-material";
import { Info } from "@material-ui/icons";
import { toast } from "react-toastify";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import WarningIcon from "@material-ui/icons/Warning";

import { format, parseISO } from "date-fns";

const getBadgeFromStatus = (
  status: string,
  translationFunction: (text: string, options?: any) => string,
  validationExpirationDate?: string,
  approvedBySuperUser?: boolean,
  manualValidationOwner?: {
    approved: boolean;
    date: string;
    email: string;
    name: string;
    comment?: string;
  },
  validationExpirationStartDate?: string,
): JSX.Element => {
  const badgeInfo = StatusBadgeMapping[status] || {
    color: "info",
    text: status,
  };

  return checkIfStringInArrayIgnoreCaseAndRemoveSpecialChars(status, [
    WorkItemResultEnum.HOMOLOGADO_MANUALMENTE,
    WorkItemResultEnum.MANUALLY_HOMOLOGATED,
    WorkItemResultEnum.APROVADO_MANUALMENTE,
    WorkItemResultEnum.APPROVED,
  ]) && manualValidationOwner ? (
    <Tooltip
      key={translationFunction(status)}
      title={
        <>
          <p>{`${translationFunction("validated-by")} ${
            manualValidationOwner?.name
          }`}</p>
          <p>{`${translationFunction("date")}: ${
            manualValidationOwner?.date
              ? format(
                  new Date(manualValidationOwner.date),
                  "dd/MM/yyyy HH:mm:ss",
                )
              : ""
          }`}</p>

          {validationExpirationStartDate && (
            <p>{`${translationFunction("initial-date")}: ${
              validationExpirationStartDate
                ? new Date(validationExpirationStartDate).toLocaleDateString(
                    "pt-br",
                    {
                      timeZone: "UTC",
                    },
                  )
                : ""
            }`}</p>
          )}

          <p>{`${translationFunction("expiration-date")}: ${
            validationExpirationDate
              ? new Date(validationExpirationDate).toLocaleDateString("pt-br", {
                  timeZone: "UTC",
                })
              : ""
          }`}</p>
        </>
      }
      placement="bottom"
    >
      <Box display="flex">
        <Badge {...{ color: badgeInfo.color }}>
          {translationFunction(status, { ns: "status" })}
          {badgeInfo.loading && (
            <CircularProgress
              size={8}
              color="inherit"
              thickness={6}
              style={{ marginLeft: "4px" }}
            />
          )}
        </Badge>
        <Info style={{ marginLeft: 5 }} color="disabled" />
      </Box>
    </Tooltip>
  ) : checkIfStringInArrayIgnoreCaseAndRemoveSpecialChars(status, [
      WorkItemResultEnum.REJEITADO_MANUALMENTE,
      WorkItemResultEnum.REJECTED,
    ]) && manualValidationOwner ? (
    <Tooltip
      key={translationFunction(status, { ns: "status" })}
      title={
        <>
          <p>{`${translationFunction("rejected-by")} ${
            manualValidationOwner?.name
          }`}</p>
          <p>{`${translationFunction("date")}: ${
            manualValidationOwner?.date
              ? format(
                  new Date(manualValidationOwner.date),
                  "dd/MM/yyyy HH:mm:ss",
                )
              : ""
          }`}</p>
        </>
      }
      placement="bottom"
    >
      <Box display="flex">
        <Badge {...{ color: badgeInfo.color }}>
          {translationFunction(status, { ns: "status" })}
          {badgeInfo.loading && (
            <CircularProgress
              size={8}
              color="inherit"
              thickness={6}
              style={{ marginLeft: "4px" }}
            />
          )}
        </Badge>
        <Info style={{ marginLeft: 5 }} color="disabled" />
      </Box>
    </Tooltip>
  ) : validationExpirationDate ? (
    <Tooltip
      key={translationFunction(status, { ns: "status" })}
      title={
        <>
          {approvedBySuperUser && <p>{"Approved by Super User"}</p>}
          <p>{`Expiration Date: ${
            validationExpirationDate
              ? format(new Date(validationExpirationDate), "dd/MM/yyyy")
              : ""
          }`}</p>
        </>
      }
      placement="top-start"
    >
      <div>
        <Badge {...{ color: badgeInfo.color }}>
          {translationFunction(status, { ns: "status" })}
          {badgeInfo.loading && (
            <CircularProgress
              size={8}
              color="inherit"
              thickness={6}
              style={{ marginLeft: "4px" }}
            />
          )}
        </Badge>
      </div>
    </Tooltip>
  ) : (
    <Badge {...{ color: badgeInfo.color }} key={translationFunction(status)}>
      {translationFunction(status, { ns: "status" })}
      {badgeInfo.loading && (
        <CircularProgress
          size={8}
          color="inherit"
          thickness={6}
          style={{ marginLeft: "4px" }}
        />
      )}
    </Badge>
  );
};

const getIconFromStatusString = (statusString: string) => {
  const componentName = StatusIconMapping[statusString];

  switch (componentName) {
    case "pending":
      return <HourglassEmpty color="warning" />;
    case "close":
      return <Close color="error" />;
    case "done":
      return <Done color="success" />;
    default:
      return null;
  }
};

const genarateBadgeFromBooleanStatus = (
  approved: boolean,
  options?: {
    validationExpirationDate?: string;
    approvedBySuperUser?: boolean;
    translationFn?: any;
  },
): JSX.Element => {
  const badgeInfo = {
    color: approved ? "success" : "danger",
    text: approved ? "approved" : "rejected",
  };

  return options?.validationExpirationDate ? (
    <Tooltip
      title={
        <>
          {options?.approvedBySuperUser && <p>{"Approved by Super User"}</p>}
          <p>{`Expiration Date: ${
            options?.validationExpirationDate
              ? format(new Date(options.validationExpirationDate), "dd/MM/yyyy")
              : ""
          }`}</p>
        </>
      }
      placement="top-start"
    >
      <Badge {...{ color: badgeInfo.color }}>
        {options?.translationFn
          ? options?.translationFn(badgeInfo.text, { ns: "status-list" })
          : badgeInfo.text}
      </Badge>
    </Tooltip>
  ) : (
    <Badge {...{ color: badgeInfo.color }}>
      {options?.translationFn
        ? options?.translationFn(badgeInfo.text, { ns: "status-list" })
        : badgeInfo.text}
    </Badge>
  );
};

function useDebounce(fn: any, delay: number): (...args: any) => void {
  const timeoutRef = useRef(null);
  function debouncedFn(...args: any) {
    clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      fn(...args);
    }, delay);
  }
  return debouncedFn;
}

const normalizeString = (str: string) =>
  str
    ?.toUpperCase()
    .replace(/[ÀÁÂÃÄÅ]/g, "A")
    .replace(/[ÈÉÊË]/g, "E")
    .replace(/[ÌÍÎÏ]/g, "I")
    .replace(/[ÒÓÔÕÖØ]/g, "O")
    .replace(/[ÙÚÛÜ]/g, "U")
    .replace(/[Ç]/g, "C")
    .replace(/[^A-Z0-9]/g, "");

function compareStringsIgnoreCaseAndRemoveSpecialChars(
  str1: string,
  str2: string,
): boolean {
  return normalizeString(str1) === normalizeString(str2);
}

function checkIfStringInArrayIgnoreCaseAndRemoveSpecialChars(
  str1: string,
  str2: string[],
): boolean {
  return str2.map(normalizeString).includes(normalizeString(str1));
}

const getFilters = (
  filters: any,
  disabledChangeSupplierNameFilter?: boolean,
) => {
  const object: { [key: string]: any } = {};

  if (filters?.length > 0)
    filters?.forEach((filter: any) => {
      const trimmedValue =
        typeof filter.value === "string" ? filter.value.trim() : filter.value;

      object[
        `filter[${
          filter.id === "supplierName" && !disabledChangeSupplierNameFilter
            ? "supplier"
            : filter.id
        }]`
      ] = trimmedValue;
    });

  return { ...object };
};

const getSorting = (sorting: any) => {
  const object: { [key: string]: any } = {};

  if (sorting?.length > 0)
    sorting?.forEach(
      (sort: any) =>
        (object[
          `sort[${sort.id === "supplierName" ? "supplier" : sort.id}]`
        ] = sort.desc ? "desc" : "asc"),
    );

  return { ...object };
};

const checkMessageErrors = (
  error: IMessagesError,
  defaultMessage: IMessagesError,
) => {
  const auxCheck =
    MessagesErrorEnum[`${error}`] != undefined
      ? MessagesErrorEnum[`${error}`]
      : MessagesErrorEnum[`${defaultMessage}`];

  return auxCheck;
};

const removeElementsFromArray = (
  sourceArray: any[],
  elementsToRemove: string[],
): any[] => {
  return sourceArray.filter((item) => !elementsToRemove.includes(item.sowID));
};

const converterParaString = function (
  dado: any,
  translationFunction: (text: string) => string,
): string {
  const dateRegex = /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}\.\d$/;

  if (dado === null || dado === undefined) {
    return "";
  } else if (typeof dado === "string") {
    if (dateRegex.test(dado)) {
      const [datePart] = dado.split(" ");
      const [year, month, day] = datePart.split("-");
      return `${day}/${month}/${year}`;
    }
    return translationFunction(dado);
  } else if (Array.isArray(dado)) {
    return dado
      .map((item) => converterParaString(item, translationFunction))
      .join(", ");
  } else if (typeof dado === "object") {
    return Object.values(dado)
      .map((item) => converterParaString(item, translationFunction))
      .join(", ");
  } else {
    return translationFunction(String(dado));
  }
};

const mapStateToUF = (state: string): string => {
  const states: { [key: string]: string } = {
    Acre: "AC",
    Alagoas: "AL",
    Amapá: "AP",
    Amazonas: "AM",
    Bahia: "BA",
    Ceará: "CE",
    "Distrito Federal": "DF",
    "Espírito Santo": "ES",
    Goiás: "GO",
    Maranhão: "MA",
    "Mato Grosso": "MT",
    "Mato Grosso do Sul": "MS",
    "Minas Gerais": "MG",
    Pará: "PA",
    Paraíba: "PB",
    Paraná: "PR",
    Pernambuco: "PE",
    Piauí: "PI",
    "Rio de Janeiro": "RJ",
    "Rio Grande do Norte": "RN",
    "Rio Grande do Sul": "RS",
    Rondônia: "RO",
    Roraima: "RR",
    "Santa Catarina": "SC",
    "São Paulo": "SP",
    Sergipe: "SE",
    Tocantins: "TO",
  };

  return states[state] || state;
};

const renderToastUnique = ({
  toastId,
  message,
  type = "info",
  options,
}: IRenderToastUnique) => {
  const loading = type === "loading";

  if (toast.isActive(toastId)) {
    toast.update(toastId, {
      render: message,
      type: type == "loading" ? "default" : type,
      autoClose: loading ? false : 3000,
      closeButton: !loading,
      isLoading: loading,
      ...options,
    });
  } else {
    toast[type](message, {
      toastId: toastId,
      autoClose: loading ? false : 3000,
      closeButton: !loading,
      ...options,
    });
  }
};

const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

function replaceKeyWithDate(
  phrase: string,
  key: string,
  isoDateString: string,
): string {
  const date = new Date(isoDateString);

  const formattedDate = date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  const formattedTime = date.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const formattedDateTime = `${formattedDate} - ${formattedTime}`;

  return phrase.replace(new RegExp(key, "g"), formattedDateTime);
}

const renderAnalysisIcon = (status: any) => {
  switch (status) {
    case WorkItemResultEnum.HOMOLOGADO:
    case WorkItemResultEnum.HOMOLOGATED:
    case WorkItemResultEnum.Approved:
      return <CheckCircleIcon style={{ color: "green" }} />;
    case WorkItemResultEnum.NAO_HOMOLOGADO:
    case WorkItemResultEnum.Not_Approved:
      return <CancelIcon style={{ color: "red" }} />;
    default:
      return <WarningIcon style={{ color: "orange" }} />;
  }
};

const parseISOWithoutTimezoneOffset = (dateString: string) => {
  const date = parseISO(dateString);
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

const parseWithoutTimezoneOffset = (isoString: string) => {
  const date = new Date(isoString);
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

const formatDate = (date: Date) => {
  return date.toLocaleDateString("pt-BR");
};

function formatDateIfValid(
  value: any,
  dateFormat: string = "dd/MM/yyyy",
): string {
  if (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/.test(value)
  ) {
    return format(new Date(value), dateFormat);
  }

  return value || "";
}

export function formatDateIsoUtc(dateString: string) {
  if (
    typeof dateString === "string" &&
    /^\d{4}-\d{2}-\d{2}T/.test(dateString)
  ) {
    const dateObj = new Date(dateString);
    const day = String(dateObj.getUTCDate()).padStart(2, "0");
    const month = String(dateObj.getUTCMonth() + 1).padStart(2, "0");
    const year = dateObj.getUTCFullYear();
    return `${day}/${month}/${year}`;
  }
  return dateString;
}

function htmlToText(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  function traverse(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || "";
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;

      let text = "";

      // Adiciona quebra de linha antes de blocos
      if (["DIV", "P", "LI", "BR"].includes(el.tagName)) {
        text += "\n";
      }

      for (const child of Array.from(el.childNodes)) {
        text += traverse(child);
      }

      // Quebra depois também ajuda na legibilidade
      if (["DIV", "P", "LI"].includes(el.tagName)) {
        text += "\n";
      }

      return text;
    }

    return "";
  }

  return traverse(doc.body)
    .replace(/\n\s*\n/g, "\n") // remove múltiplas linhas vazias
    .replace(/[ \t]+/g, " ") // normaliza espaços
    .trim();
}

export {
  checkIfStringInArrayIgnoreCaseAndRemoveSpecialChars,
  compareStringsIgnoreCaseAndRemoveSpecialChars,
  genarateBadgeFromBooleanStatus,
  getBadgeFromStatus,
  getIconFromStatusString,
  useDebounce,
  getFilters,
  getSorting,
  checkMessageErrors,
  removeElementsFromArray,
  converterParaString,
  mapStateToUF,
  renderToastUnique,
  formatBytes,
  replaceKeyWithDate,
  renderAnalysisIcon,
  parseISOWithoutTimezoneOffset,
  parseWithoutTimezoneOffset,
  formatDateIfValid,
  formatDate,
  htmlToText,
};
